import { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Cookie, X } from '@phosphor-icons/react';

const STORAGE_KEY = 'solefinder_cookie_consent_v1';

// Routes where the banner is hidden — focused conversion flows where the
// banner can cover bottom CTAs on small mobile viewports.
const HIDE_ON_PREFIXES = ['/quiz', '/finder/', '/results/'];

// Re-prompt only if 30+ days have passed without a choice. Once dismissed,
// the user is left alone for a month (CCPA/GDPR-compatible — they always
// have access to /privacy to change preferences any time).
const REPROMPT_AFTER_DAYS = 30;

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let saved;
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { saved = null; }

    if (!saved) {
      const t = setTimeout(() => setVisible(true), 900);
      return () => clearTimeout(t);
    }

    // Re-prompt if older than REPROMPT_AFTER_DAYS days
    if (saved.at) {
      const ageMs = Date.now() - new Date(saved.at).getTime();
      const ageDays = ageMs / (1000 * 60 * 60 * 24);
      if (ageDays > REPROMPT_AFTER_DAYS) {
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    }
  }, []);

  const save = (choice) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
    setVisible(false);
    window.dispatchEvent(new CustomEvent('solefinder:cookie-consent', { detail: choice }));
  };

  const onConversionRoute = HIDE_ON_PREFIXES.some(p => location.pathname.startsWith(p));
  if (!visible || onConversionRoute) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6 z-[9998] sm:max-w-[360px] animate-in fade-in slide-in-from-bottom-4 duration-300"
    >
      <div className="bg-[#0A0A0A] text-white border-2 border-[#FF3B30] shadow-2xl">
        <div className="p-4 sm:p-5">
          <div className="flex items-start gap-3 mb-3">
            <Cookie size={22} weight="duotone" className="text-[#FF3B30] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-bold text-white uppercase tracking-wide text-[11px] mb-1.5">We use cookies</p>
              <p className="text-[12.5px] leading-relaxed text-[#D4D4D8]">
                Essential cookies keep the site running. Optional analytics help us improve.{' '}
                <Link to="/privacy" className="underline hover:text-[#FF3B30] text-[#A1A1AA]">Privacy Policy</Link>
              </p>
            </div>
            <button
              type="button"
              onClick={() => save('essential')}
              aria-label="Dismiss cookie banner"
              className="flex-shrink-0 -mt-1 -mr-1 w-7 h-7 inline-flex items-center justify-center text-[#71717A] hover:text-white transition-colors"
              data-testid="cookie-consent-close-btn"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => save('essential')}
              className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide bg-transparent text-white border border-white/20 hover:border-white hover:bg-white/5 transition-colors"
              data-testid="cookie-consent-essential-btn"
            >
              Essential only
            </button>
            <button
              type="button"
              onClick={() => save('all')}
              className="px-3 py-2.5 text-[11px] font-bold uppercase tracking-wide bg-[#FF3B30] text-white hover:bg-[#D62828] transition-colors"
              data-testid="cookie-consent-accept-btn"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
